/**
 * Sitemap utilities for SEO optimization
 */

export interface SitemapUrl {
  loc: string
  lastmod?: string
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
  priority?: number
}

export class SitemapGenerator {
  private baseUrl: string

  constructor(baseUrl: string = window.location.origin) {
    this.baseUrl = baseUrl
  }

  /**
   * Generate sitemap URLs for footer pages
   */
  getFooterPageUrls(): SitemapUrl[] {
    const lastmod = new Date().toISOString().split('T')[0]
    
    return [
      {
        loc: `${this.baseUrl}/privacy-policy`,
        lastmod,
        changefreq: 'monthly',
        priority: 0.6
      },
      {
        loc: `${this.baseUrl}/terms-of-service`,
        lastmod,
        changefreq: 'monthly',
        priority: 0.6
      },
      {
        loc: `${this.baseUrl}/contact`,
        lastmod,
        changefreq: 'monthly',
        priority: 0.7
      }
    ]
  }

  /**
   * Generate sitemap URLs for main application pages
   */
  getMainPageUrls(): SitemapUrl[] {
    const lastmod = new Date().toISOString().split('T')[0]
    
    return [
      {
        loc: this.baseUrl,
        lastmod,
        changefreq: 'daily',
        priority: 1.0
      },
      {
        loc: `${this.baseUrl}/categories`,
        lastmod,
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${this.baseUrl}/search`,
        lastmod,
        changefreq: 'weekly',
        priority: 0.7
      },
      {
        loc: `${this.baseUrl}/favorites`,
        lastmod,
        changefreq: 'weekly',
        priority: 0.5
      }
    ]
  }

  /**
   * Generate complete sitemap XML
   */
  generateSitemapXML(): string {
    const allUrls = [
      ...this.getMainPageUrls(),
      ...this.getFooterPageUrls()
    ]

    const urlElements = allUrls.map(url => {
      let urlXml = `    <url>\n      <loc>${url.loc}</loc>\n`
      
      if (url.lastmod) {
        urlXml += `      <lastmod>${url.lastmod}</lastmod>\n`
      }
      
      if (url.changefreq) {
        urlXml += `      <changefreq>${url.changefreq}</changefreq>\n`
      }
      
      if (url.priority !== undefined) {
        urlXml += `      <priority>${url.priority}</priority>\n`
      }
      
      urlXml += '    </url>'
      return urlXml
    }).join('\n')

    return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlElements}
</urlset>`
  }

  /**
   * Generate robots.txt content
   */
  generateRobotsTxt(): string {
    return `User-agent: *
Allow: /

# Sitemap
Sitemap: ${this.baseUrl}/sitemap.xml

# Crawl-delay for polite crawling
Crawl-delay: 1

# Disallow admin areas
Disallow: /admin/
Disallow: /admin/*

# Allow important pages
Allow: /privacy-policy
Allow: /terms-of-service
Allow: /contact
Allow: /categories
Allow: /search
Allow: /wallpaper/*`
  }
}

/**
 * Add structured data for breadcrumbs
 */
export function addBreadcrumbStructuredData(breadcrumbs: Array<{ name: string; url: string }>) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  }

  // Remove existing breadcrumb structured data
  const existingScript = document.getElementById('breadcrumb-structured-data')
  if (existingScript) {
    document.head.removeChild(existingScript)
  }

  // Add new breadcrumb structured data
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(structuredData, null, 2)
  script.id = 'breadcrumb-structured-data'
  document.head.appendChild(script)
}

/**
 * Remove breadcrumb structured data
 */
export function removeBreadcrumbStructuredData() {
  const script = document.getElementById('breadcrumb-structured-data')
  if (script) {
    document.head.removeChild(script)
  }
}